import { useMutation, useQuery } from "@tanstack/react-query";
import { getRouteApi, Link, useNavigate } from "@tanstack/react-router";
import { AppIcon, Badge, Button, Notice, Plate, SelectField, TextField } from "@wilco/ui";
import { useDeferredValue, useState, type SubmitEvent } from "react";
import { t, type MessageKey } from "../i18n.ts";
import { errorNotice, type NoticeState } from "../stock/ShelfPage.tsx";
import { catalogApi, variantName, type Match } from "./api.ts";
import { categoriesQuery, CATALOG } from "./queries.ts";

const route = getRouteApi("/app/catalog/products");

const MATCHED: Record<Match["matchedBy"], MessageKey> = {
  OWN_CODE: "catalog.match.OWN_CODE",
  OWN_NAME: "catalog.match.OWN_NAME",
  SUPPLIER_CODE: "catalog.match.SUPPLIER_CODE",
  SUPPLIER_NAME: "catalog.match.SUPPLIER_NAME",
};

/**
 * Товары: поиск по своему и чужому артикулу и названию (CAT-08, ЗК-23).
 * Найденный по названию или артикулу поставщика показан с этим псевдонимом:
 * так закупщик видит, как товар называется у нас.
 */
export function ProductsPage() {
  const { q = "" } = route.useSearch();
  const navigate = useNavigate();
  const [text, setText] = useState(q);
  const query = useDeferredValue(text.trim());
  const found = useQuery({ queryKey: [...CATALOG, "search", query], queryFn: () => catalogApi.search(query), enabled: query.length >= 2 });

  return (
    <div className="page catalog">
      <div className="page-head">
        <h1 className="page-title page-title-icon">
          <AppIcon name="stock" size={32} />
          {t("catalog.title")}
        </h1>
        <Button asChild variant="create">
          <Link to="/catalog/products/new">{t("catalog.new")}</Link>
        </Button>
      </div>
      <form
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          void navigate({ to: "/catalog/products", search: { q: text.trim() || undefined }, replace: true });
        }}
      >
        <TextField label={t("catalog.search")} placeholder={t("catalog.searchHint")} value={text} onChange={(e) => setText(e.target.value)} autoFocus autoComplete="off" />
      </form>
      {found.error ? <Notice tone="danger" title={errorNotice(found.error).title} className="stock-notice" /> : null}
      {query.length < 2 ? <p className="w-muted stock-hint">{t("catalog.searchLead")}</p> : null}
      {found.data?.length === 0 ? <p className="w-muted stock-hint">{t("palette.empty")}</p> : null}
      <ul className="catalog-results">
        {found.data?.map((m) => (
          <li key={m.product.id}>
            <Link to="/catalog/templates/$id" params={{ id: m.product.templateId }} className="catalog-result">
              <span className="catalog-result-main">
                <span>
                  <span className="w-num shelf-sku">{m.product.code ?? "—"}</span> {variantName(m.product)}
                </span>
                <span className="w-muted catalog-result-meta">
                  {m.product.categoryPath}
                  {m.matchedBy === "SUPPLIER_CODE" || m.matchedBy === "SUPPLIER_NAME" ? ` · ${t(MATCHED[m.matchedBy], { alias: m.alias ?? "", supplier: m.supplierPartner ?? "" })}` : ""}
                </span>
              </span>
              {m.product.archived ? <Badge>{t("catalog.archived")}</Badge> : null}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Новый товар: артикул ТИП-NNNN даёт сервер, дубль названия он отклоняет (CAT-01, CAT-06). */
export function NewProductPage() {
  const navigate = useNavigate();
  const categories = useQuery(categoriesQuery);
  const [name, setName] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [code, setCode] = useState("");
  const [consumable, setConsumable] = useState(false);
  const [imported, setImported] = useState(false);
  const [notice, setNotice] = useState<NoticeState | null>(null);
  const create = useMutation({ mutationFn: catalogApi.createTemplate });

  const submit = (e: SubmitEvent) => {
    e.preventDefault();
    setNotice(null);
    const cat = categories.data?.find((c) => c.id === categoryId);
    create.mutate(
      {
        name,
        categoryId,
        code: code.trim() || null,
        kind: cat?.kind ?? null,
        tags: [...(consumable ? (["CONSUMABLE"] as const) : []), ...(imported ? (["IMPORT"] as const) : [])],
        labelsPerUnit: cat?.kind === "PRODUCT" ? 4 : 0,
        versionCodes: {},
        versions: [],
      },
      {
        onSuccess: (card) => void navigate({ to: "/catalog/templates/$id", params: { id: card.id } }),
        onError: (err) => setNotice(errorNotice(err)),
      },
    );
  };

  return (
    <div className="page stock-narrow">
      <h1 className="page-title">{t("catalog.new")}</h1>
      <Plate cut="lg">
        <form className="stock-form catalog-form" onSubmit={submit}>
          <TextField label={t("catalog.name")} value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
          <SelectField
            label={t("catalog.category")}
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            required
            options={[{ value: "", label: t("catalog.chooseCategory") }, ...(categories.data ?? []).map((c) => ({ value: c.id, label: c.path }))]}
          />
          <TextField label={t("catalog.code")} hint={t("catalog.codeHint")} value={code} onChange={(e) => setCode(e.target.value)} autoComplete="off" />
          <label className="check">
            <input type="checkbox" checked={consumable} onChange={(e) => setConsumable(e.target.checked)} />
            {t("catalog.tag.CONSUMABLE")}
          </label>
          <label className="check">
            <input type="checkbox" checked={imported} onChange={(e) => setImported(e.target.checked)} />
            {t("catalog.tag.IMPORT")}
          </label>
          {notice ? <Notice tone={notice.tone} title={notice.title} /> : null}
          <Button type="submit" variant="primary" disabled={create.isPending || !name.trim() || !categoryId}>
            {t("catalog.create")}
          </Button>
        </form>
      </Plate>
    </div>
  );
}
